import React, { useRef } from "react";
import { Input } from "antd";
import type { InputRef } from "antd";
import { formatWholePrice, parseWholePrice } from "../utils/price";

type PriceInputProps = {
  value?: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

const cursorPositionForDigitCount = (
  formattedValue: string,
  digitCount: number,
): number => {
  if (digitCount === 0) return 0;

  let seenDigits = 0;
  for (let index = 0; index < formattedValue.length; index += 1) {
    if (/\d/.test(formattedValue[index])) seenDigits += 1;
    if (seenDigits === digitCount) return index + 1;
  }

  return formattedValue.length;
};

export const PriceInput = ({
  value,
  onChange,
  disabled,
  className,
  style,
}: PriceInputProps) => {
  const inputRef = useRef<InputRef>(null);

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label="Price"
      className={className}
      style={style}
      disabled={disabled}
      value={formatWholePrice(value)}
      onChange={(event) => {
        const rawValue = event.target.value;
        const cursorPosition = event.target.selectionStart ?? rawValue.length;
        const digitsBeforeCursor = rawValue
          .slice(0, cursorPosition)
          .replace(/\D/g, "").length;
        const nextValue = parseWholePrice(rawValue);
        const formattedValue = formatWholePrice(nextValue);

        onChange(nextValue);

        requestAnimationFrame(() => {
          const input = inputRef.current?.input;
          if (!input) return;
          const nextCursorPosition = cursorPositionForDigitCount(
            formattedValue,
            digitsBeforeCursor,
          );
          input.setSelectionRange(nextCursorPosition, nextCursorPosition);
        });
      }}
    />
  );
};

